// Custom-styled, server-searched + paginated dropdown for the header breadcrumbs — not a
// port of anything in LC2 (LC2's old <select>-style breadcrumb pickers only ever listed
// the client-side `dashboard.communities`/`dashboard.campaigns` arrays, no search or
// pagination at all). Built against the two new endpoints added specifically for this:
// `searchCommunities`/`searchCampaigns` (see features/coreApi/communitiesSlice.js for the
// confirmed request/response shape).
//
// Pagination note: the confirmed response carries its own cursor —
// {..., "pagination": {"limit": 100, "offset": 1, "total_count": 89060, "next_offset": 101}}
// — so the next page is fetched using whatever `nextOffset` the last response returned
// (not `offset + limit`, and not a client-incremented page index), and `totalCount` is the
// authoritative signal for when to stop, since it's not safe to infer "no more" just from
// a page coming back short. Infinite-scroll-triggered (same threshold-based onScroll
// pattern as Crm/ResidentsTable.js) instead of a "Load more" button.
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, ButtonBase, InputBase, Popper, ClickAwayListener, Stack, Typography, CircularProgress} from '@mui/material';
import {tokens} from '../../themes/ThemeCustomization';

import breadcrumbChevronIcon from '../../assets/shell/breadcrumb-chevron-icon.svg';
import tableSearchIcon from '../../assets/shell/table-search-icon.svg';

const useDebouncedValue = (value, delayMs) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);
    return debounced;
};

const SCROLL_THRESHOLD_PX = 80;

const SearchableBreadcrumbDropdown = ({label, placeholder, allOption, useSearchQuery, accountSfid, communitySfid, onSelect}) => {
    const anchorRef = useRef(null);
    const listRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [offset, setOffset] = useState(0);

    const search = useDebouncedValue(inputValue, 300);

    const {data, isFetching, isError} = useSearchQuery({accountSfid, communitySfid, search, offset}, {skip: !open || !accountSfid});
    const results = data?.items || [];

    // The backend is the source of truth for whether another page exists: `nextOffset` is
    // null/undefined once the result set is exhausted, and `totalCount` is a second,
    // independent signal to stop once everything's loaded (belt-and-suspenders in case
    // `nextOffset` is ever returned stale) — total counts here run into the tens of
    // thousands, so a short page is not itself evidence there's nothing left.
    const hasMore = Boolean(data?.nextOffset) && (data?.totalCount == null || results.length < data.totalCount);

    // Reset pagination whenever the dropdown (re)opens or the search term changes.
    useEffect(() => {
        setOffset(0);
    }, [search, open]);

    const handleOpen = () => {
        setInputValue('');
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleSelect = (option) => {
        setOpen(false);
        onSelect(option);
    };

    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el || isFetching || !hasMore || !data?.nextOffset) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < SCROLL_THRESHOLD_PX) {
            setOffset(data.nextOffset);
        }
    }, [isFetching, hasMore, data?.nextOffset]);

    const showAllOption = allOption && !search;
    const list = showAllOption ? [allOption, ...results] : results;

    return (
        <Box ref={anchorRef} sx={{position: 'relative'}}>
            <ButtonBase
                onClick={handleOpen}
                sx={{borderRadius: '6px', pl: 1.5, pr: 0.25, py: 1, gap: 0.75}}
            >
                <Typography variant="body2" fontWeight={500} noWrap sx={{maxWidth: '260px'}}>
                    {label}
                </Typography>
                <Box component="img" src={breadcrumbChevronIcon} alt="" sx={{width: 16, height: 16}} />
            </ButtonBase>

            <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{zIndex: 1300}}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Box
                        sx={{
                            mt: 0.5,
                            width: '280px',
                            maxHeight: '360px',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: tokens.color.white,
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '8px',
                            boxShadow: tokens.shadow.toast,
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{p: 1, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    border: `1px solid ${tokens.color.neutral200}`,
                                    borderRadius: '6px',
                                    height: 32,
                                    px: 1
                                }}
                            >
                                <Box component="img" src={tableSearchIcon} alt="" sx={{width: 14, height: 14}} />
                                <InputBase
                                    autoFocus
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={placeholder}
                                    sx={{flex: 1, fontSize: '14px'}}
                                />
                            </Box>
                        </Box>

                        <Box ref={listRef} onScroll={handleScroll} sx={{overflowY: 'auto', flex: 1}}>
                            {isError && (
                                <Typography variant="body2" color="error.main" sx={{p: 2, textAlign: 'center'}}>
                                    Something went wrong loading this list.
                                </Typography>
                            )}
                            {!isError && list.length === 0 && !isFetching && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 2, textAlign: 'center'}}>
                                    No results.
                                </Typography>
                            )}
                            {!isError && list.map((option) => (
                                <ButtonBase
                                    key={option.sfid}
                                    onClick={() => handleSelect(option)}
                                    sx={{
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        px: 1.5,
                                        py: 1,
                                        '&:hover': {backgroundColor: tokens.color.neutral100}
                                    }}
                                >
                                    <Typography variant="body2" noWrap>
                                        {option.name}
                                    </Typography>
                                </ButtonBase>
                            ))}
                            {isFetching && (
                                <Stack alignItems="center" sx={{py: 1.5}}>
                                    <CircularProgress size={16} />
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default SearchableBreadcrumbDropdown;
